import { PRODUCT_IMAGE_PLACEHOLDER, isInvalidProductImage } from "../components/common/ProductImage";

const BANNER_FALLBACK_THEMES = [
    {
        background: "#eef8f3",
        titleColor: "#18243a",
        subtitleColor: "#516070",
        eyebrowColor: "#208a54",
        badgeBackground: "#2ECC71",
        badgeColor: "#ffffff",
        buttonBackground: "#30358b",
        buttonColor: "#ffffff",
    },
    {
        background: "#fff3e4",
        titleColor: "#342216",
        subtitleColor: "#765943",
        eyebrowColor: "#d36b12",
        badgeBackground: "#ff8a00",
        badgeColor: "#ffffff",
        buttonBackground: "#7b3f16",
        buttonColor: "#ffffff",
    },
    {
        background: "#eef3ff",
        titleColor: "#18264d",
        subtitleColor: "#4d5d82",
        eyebrowColor: "#3554d1",
        badgeBackground: "#3554d1",
        badgeColor: "#ffffff",
        buttonBackground: "#16a085",
        buttonColor: "#ffffff",
    },
];

const parseJsonValue = (value) => {
    if (!value || typeof value !== "string") return null;

    const trimmedValue = value.trim();

    if (!trimmedValue || !["{", "["].includes(trimmedValue[0])) return null;

    try {
        return JSON.parse(trimmedValue);
    } catch (error) {
        return null;
    }
};

const asObject = (value) => {
    if (value && typeof value === "object" && !Array.isArray(value)) return value;
    return parseJsonValue(value) || {};
};

const hasObjectKeys = (value) =>
    value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;

const getSectionBanners = (sections) => {
    if (!Array.isArray(sections)) return null;

    const bannerSection = sections.find((section) => {
        const type = String(section?.componentType || section?.component_type || section?.type || "").toLowerCase();
        return ["hero_carousel", "hero_banner", "banner", "banners", "banner_carousel"].includes(type);
    });

    if (!bannerSection) return null;
    if (Array.isArray(bannerSection.data)) return bannerSection.data;
    if (hasObjectKeys(bannerSection.data)) return [bannerSection.data];

    return null;
};

const getCellContents = (data) => {
    const source = parseJsonValue(data) || data;
    const candidates = [
        source?.cell_contents,
        source?.response_data?.[0]?.cell_contents,
        source?.response_data?.[0]?.banners,
        source?.response_data?.[0]?.banner_list,
        source?.response_data?.[0]?.bannerList,
        source?.response_data?.[0]?.items,
        source?.response_data?.[0]?.list,
        getSectionBanners(source?.response_data?.[0]?.sections),
        source?.response_data?.cell_contents,
        source?.response_data,
        source?.data?.cell_contents,
        source?.data?.response_data?.[0]?.cell_contents,
        source?.data?.response_data?.[0]?.banners,
        source?.data?.response_data?.[0]?.banner_list,
        source?.data?.response_data?.[0]?.bannerList,
        source?.data?.response_data?.[0]?.items,
        source?.data?.response_data?.[0]?.list,
        getSectionBanners(source?.data?.response_data?.[0]?.sections),
        source?.data?.response_data?.cell_contents,
        source?.data?.response_data,
        source?.banners,
        source?.banner_list,
        source?.bannerList,
        source?.items,
        source?.list,
        getSectionBanners(source?.sections),
        source?.data?.banners,
        source?.data?.banner_list,
        source?.data?.bannerList,
        source?.data?.items,
        source?.data?.list,
        getSectionBanners(source?.data?.sections),
        source?.data,
        source,
    ];

    const matchedList = candidates.find((item) => Array.isArray(item));

    if (matchedList) {
        const nestedList = matchedList.flatMap((item) => {
            const nestedSource = parseJsonValue(item) || item;
            return [
                nestedSource?.cell_contents,
                nestedSource?.banners,
                nestedSource?.banner_list,
                nestedSource?.bannerList,
                nestedSource?.items,
            ].find((nestedItem) => Array.isArray(nestedItem)) || [];
        });

        return nestedList.length ? nestedList.flat() : matchedList;
    }
    if (hasObjectKeys(source)) return [source];

    return [];
};

const getValue = (item, keys, fallback = "") => {
    const matchedKey = keys.find(
        (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ""
    );

    return matchedKey ? item[matchedKey] : fallback;
};

const toRenderableText = (value, fallback = "", seen = new WeakSet()) => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string") return value.trim() || fallback;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) {
        const flattened = value
            .map((entry) => toRenderableText(entry, "", seen))
            .filter(Boolean)
            .join(" ");

        return flattened || fallback;
    }
    if (typeof value === "object") {
        if (seen.has(value)) return fallback;

        seen.add(value);
        const candidate = getValue(value, ["text", "label", "title", "name", "value", "caption"], fallback);

        if (candidate === value) return fallback;
        return typeof candidate === "string"
            ? candidate.trim() || fallback
            : toRenderableText(candidate, fallback, seen);
    }

    return fallback;
};

const getObjectText = (value, fallback = "") => {
    if (value && typeof value === "object") {
        return toRenderableText(getValue(value, ["text", "label", "title", "name", "value", "caption"], fallback), fallback, new WeakSet());
    }

    return toRenderableText(value, fallback, new WeakSet());
};

const getStringValue = (item, keys, fallback = "") =>
    getObjectText(getValue(item, keys, fallback), fallback);

const getAssetUrl = (value) => {
    if (isInvalidProductImage(value)) return PRODUCT_IMAGE_PLACEHOLDER;
    const path = String(value || "").trim();

    if (/^(https?:|data:|blob:|\/)/i.test(path)) return path;

    return `${process.env.PUBLIC_URL || ""}/${path.replace(/^\/+/, "")}`;
};

const getImageValue = (value) => {
    if (Array.isArray(value)) return getImageValue(value.find(Boolean));
    if (value && typeof value === "object") {
        return getValue(value, ["url", "src", "path", "image", "image_url", "imageUrl", "file", "file_url", "fileUrl"]);
    }

    return value;
};

const getBannerJsonObject = (banner) => {
    if (typeof banner !== "object" || !banner) return {};

    return asObject(
        banner.web_banner_payload ||
        banner.banner_payload ||
        banner.banner_json ||
        banner.bannerJson ||
        banner.json ||
        banner.meta_json ||
        banner.metaJson ||
        banner.content ||
        banner.banner_content ||
        banner.bannerContent ||
        banner.design ||
        banner.json_object ||
        banner.jsonObject ||
        banner.payload
    );
};

const normalizeBannerObject = (banner) => {
    const parsedBanner = asObject(banner);
    const bannerObject = hasObjectKeys(parsedBanner)
        ? parsedBanner
        : (typeof banner === "object" && banner ? banner : {});
    const nestedData = hasObjectKeys(bannerObject.data) ? bannerObject.data : {};

    return {
        ...bannerObject,
        ...nestedData,
        ...getBannerJsonObject(bannerObject),
        ...getBannerJsonObject(nestedData),
    };
};

const getBannerImage = (banner) => {
    if (typeof banner === "string") {
        const parsedBanner = asObject(banner);
        return hasObjectKeys(parsedBanner) ? getBannerImage(parsedBanner) : getAssetUrl(banner);
    }

    const rawImage =
        banner?.imageSet ||
        banner?.image_set ||
        banner?.images ||
        banner?.media ||
        (typeof banner?.image === "object" ? banner?.image : {});
    const image = asObject(Array.isArray(rawImage) ? rawImage.find((item) => hasObjectKeys(item)) : rawImage);
    const directImage =
        getImageValue(banner?.image) ||
        getImageValue(rawImage) ||
        getImageValue(banner?.banner);

    return getAssetUrl(
        getImageValue(image.desktop) ||
        getImageValue(image.desktop_url) ||
        getImageValue(image.desktopUrl) ||
        getImageValue(image.url) ||
        getImageValue(image.src) ||
        banner?.desktop_image ||
        banner?.desktopImage ||
        banner?.desktop_url ||
        banner?.desktopUrl ||
        banner?.web_image ||
        banner?.webImage ||
        banner?.web_image_url ||
        banner?.webImageUrl ||
        banner?.image_url ||
        banner?.imageUrl ||
        banner?.image_path ||
        banner?.imagePath ||
        banner?.banner_url ||
        banner?.bannerUrl ||
        banner?.background_image ||
        banner?.backgroundImage ||
        banner?.banner_image ||
        banner?.bannerImage ||
        banner?.file_path ||
        banner?.filePath ||
        banner?.file_url ||
        banner?.fileUrl ||
        banner?.photo ||
        banner?.photo_url ||
        banner?.photoUrl ||
        directImage
    );
};

const getBannerImageSet = (banner) => {
    const rawImage =
        banner?.imageSet ||
        banner?.image_set ||
        banner?.images ||
        banner?.media ||
        (typeof banner?.image === "object" ? banner?.image : {});
    const image = asObject(Array.isArray(rawImage) ? rawImage.find((item) => hasObjectKeys(item)) : rawImage);
    const directImage =
        getImageValue(banner?.image) ||
        getImageValue(rawImage) ||
        getImageValue(banner?.banner);
    const desktop = getAssetUrl(
        getImageValue(image.desktop) ||
        getImageValue(image.desktop_url) ||
        getImageValue(image.desktopUrl) ||
        getImageValue(image.url) ||
        getImageValue(image.src) ||
        banner?.desktop_image ||
        banner?.desktopImage ||
        banner?.desktop_url ||
        banner?.desktopUrl ||
        banner?.web_image ||
        banner?.webImage ||
        banner?.web_image_url ||
        banner?.webImageUrl ||
        banner?.image_url ||
        banner?.imageUrl ||
        banner?.image_path ||
        banner?.imagePath ||
        banner?.banner_url ||
        banner?.bannerUrl ||
        banner?.background_image ||
        banner?.backgroundImage ||
        banner?.banner_image ||
        banner?.bannerImage ||
        banner?.file_path ||
        banner?.filePath ||
        banner?.file_url ||
        banner?.fileUrl ||
        banner?.photo ||
        banner?.photo_url ||
        banner?.photoUrl ||
        directImage
    );
    const tablet = getAssetUrl(
        getImageValue(image.tablet) ||
        getImageValue(image.tablet_url) ||
        getImageValue(image.tabletUrl) ||
        banner?.tablet_image ||
        banner?.tabletImage ||
        banner?.tablet_url ||
        banner?.tabletUrl ||
        getImageValue(image.desktop) ||
        desktop
    );
    const mobile = getAssetUrl(
        getImageValue(image.mobile) ||
        getImageValue(image.mobile_url) ||
        getImageValue(image.mobileUrl) ||
        banner?.mobile_image ||
        banner?.mobileImage ||
        banner?.mobile_url ||
        banner?.mobileUrl ||
        getImageValue(image.tablet) ||
        getImageValue(image.desktop) ||
        desktop
    );

    return {
        desktop,
        tablet,
        mobile,
        position: image.position || banner?.image_position || banner?.imagePosition || "center center",
    };
};

const getBannerId = (banner, index) =>
    getValue(banner, ["id", "banner_id", "bannerId"], `banner-${index}`);

const getPathFromAction = (action) => {
    if (!action) return "";

    if (typeof action === "string") {
        const trimmedAction = action.trim();
        return trimmedAction.startsWith("{") ? getPathFromAction(asObject(trimmedAction)) : trimmedAction;
    }

    const normalizedAction = asObject(action);
    const type = String(
        getStringValue(normalizedAction, ["type", "action_type", "actionType", "target_type", "targetType"])
    ).toLowerCase();
    const id = getStringValue(normalizedAction, [
        "id",
        "store_id",
        "storeId",
        "business_segment_id",
        "businessSegmentId",
        "segment_id",
        "segmentId",
        "service_id",
        "serviceId",
        "category_id",
        "categoryId",
        "product_id",
        "productId",
    ]);
    const path = getStringValue(normalizedAction, [
        "path",
        "url",
        "link",
        "href",
        "redirect_path",
        "redirectPath",
        "redirect_url",
        "redirectUrl",
    ]);

    if (path) return path;
    if ((type === "store" || type === "business_segment") && id) {
        return `/category/store-${encodeURIComponent(id)}`;
    }
    if ((type === "service" || type === "segment") && id) {
        return `/stores/${encodeURIComponent(id)}`;
    }
    if (type === "product") return "/product-details-two";

    return "";
};

const getBannerHref = (banner) => {
    const button = asObject(banner?.button || banner?.cta || banner?.primary_action || banner?.primaryAction);
    const actionPath =
        getPathFromAction(button.action) ||
        getPathFromAction(button) ||
        getPathFromAction(banner?.action);
    const directPath = getStringValue(banner, [
        "path",
        "redirect_path",
        "redirectPath",
        "redirect_url",
        "redirectUrl",
        "link",
        "url",
        "href",
    ]);

    return actionPath || directPath || "";
};

const getBannerContent = (banner, index = 0) => {
    const normalizedBanner = normalizeBannerObject(banner);
    const fallbackTheme = BANNER_FALLBACK_THEMES[index % BANNER_FALLBACK_THEMES.length];
    const mediaObject = asObject(normalizedBanner.media);
    const mediaAlt = getStringValue(mediaObject, ["alt", "alt_text", "altText"], "");
    const responsiveConfig = asObject(normalizedBanner.responsive);
    const badgeValue = normalizedBanner.badge;
    const discountBadgeValue = normalizedBanner.discount_badge || normalizedBanner.discountBadge;
    const buttonValue =
        normalizedBanner.button ||
        normalizedBanner.cta ||
        normalizedBanner.primary_action ||
        normalizedBanner.primaryAction;
    const badge = asObject(badgeValue);
    const discountBadge = asObject(discountBadgeValue);
    const button = asObject(buttonValue);
    const buttonNestedStyle = asObject(button.style);
    const style = asObject(
        normalizedBanner.style ||
        normalizedBanner.styles ||
        normalizedBanner.colors ||
        normalizedBanner.theme
    );
    const explicitEyebrow = getStringValue(
        normalizedBanner,
        ["eyebrow", "label", "tag", "offer_text", "offerText"]
    );
    const fallbackDiscountText =
        getStringValue(normalizedBanner, ["discount_text", "discountText", "coupon", "badge_text", "badgeText"]) ||
        (typeof badgeValue === "string" ? badgeValue : "");
    const title = toRenderableText(
        getStringValue(
            normalizedBanner,
            ["title", "banner_title", "bannerTitle", "name", "heading", "main_text", "mainText"]
        ),
        ""
    );
    const subtitle = toRenderableText(
        getStringValue(
            normalizedBanner,
            ["subtitle", "description", "sub_title", "subTitle", "text", "banner_text", "bannerText"]
        ),
        ""
    );
    const eyebrow = toRenderableText(explicitEyebrow || (hasObjectKeys(badge) ? getObjectText(badge) : ""), "");
    const discountText = toRenderableText(
        getObjectText(
            discountBadgeValue || discountBadge,
            fallbackDiscountText
        ),
        ""
    );
    const buttonLabel = toRenderableText(
        getObjectText(
            buttonValue || button,
            getValue(normalizedBanner, ["button_text", "buttonText", "cta", "cta_text", "ctaText"])
        ),
        ""
    );

    const accentColor =
        getStringValue(style, ["accent_color", "accentColor", "accent", "primary"]) ||
        fallbackTheme.badgeBackground;
    const rawOverlayOpacity = style.overlay_opacity ?? style.overlayOpacity;
    const parsedOverlayOpacity = Number(rawOverlayOpacity);
    const overlayOpacity = Number.isFinite(parsedOverlayOpacity) ? parsedOverlayOpacity : 0.15;
    const layout = getStringValue(style, ["layout"], "image_right") === "image_left" ? "image_left" : "image_right";

    const toRatio = (breakpoint) => {
        const width = Number(breakpoint?.width);
        const height = Number(breakpoint?.height);
        return width && height ? `${width} / ${height}` : null;
    };

    return {
        ...normalizedBanner,
        title,
        subtitle,
        eyebrow,
        discountText,
        buttonLabel,
        mediaAlt,
        buttonStyle: {
            background:
                getStringValue(buttonNestedStyle, ["background", "backgroundColor", "background_color", "bg_color"]) ||
                getStringValue(button, ["background", "backgroundColor", "background_color", "bg_color"]) ||
                accentColor ||
                fallbackTheme.buttonBackground,
            color:
                getStringValue(buttonNestedStyle, ["color", "textColor", "text_color"]) ||
                getStringValue(button, ["color", "textColor", "text_color"]) ||
                fallbackTheme.buttonColor,
        },
        discountStyle: {
            background:
                getStringValue(discountBadge, ["background", "backgroundColor", "background_color", "bg_color"]) ||
                accentColor ||
                fallbackTheme.badgeBackground,
            color:
                getStringValue(discountBadge, ["color", "textColor", "text_color"]) ||
                fallbackTheme.badgeColor,
        },
        imageSet: getBannerImageSet(normalizedBanner),
        href: getBannerHref(normalizedBanner),
        style: {
            background:
                getStringValue(style, ["background", "backgroundColor", "background_color", "bg_color"]) ||
                getStringValue(normalizedBanner, ["background", "backgroundColor", "background_color", "bg_color"]) ||
                fallbackTheme.background,
            titleColor:
                getStringValue(style, ["title_color", "titleColor", "title", "heading_color", "headingColor", "text_color", "textColor"]) ||
                fallbackTheme.titleColor,
            subtitleColor:
                getStringValue(style, ["subtitle_color", "subtitleColor", "desc_color", "descColor", "description_color", "descriptionColor", "text_color", "textColor"]) ||
                fallbackTheme.subtitleColor,
            eyebrowColor:
                getStringValue(style, ["eyebrow_color", "eyebrowColor", "label_color", "labelColor", "accent", "primary", "accent_color", "accentColor"]) ||
                fallbackTheme.eyebrowColor,
            accentColor,
            overlayOpacity,
            layout,
        },
        responsiveRatios: {
            desktop: toRatio(responsiveConfig.desktop),
            tablet: toRatio(responsiveConfig.tablet),
            mobile: toRatio(responsiveConfig.mobile),
        },
    };
};

const hasBannerContent = (bannerContent) =>
    Boolean(
        bannerContent.title ||
        bannerContent.subtitle ||
        bannerContent.eyebrow ||
        bannerContent.discountText ||
        bannerContent.buttonLabel
    );

export { asObject, getAssetUrl, getBannerContent, getBannerId, getBannerImage, getCellContents, getValue, hasBannerContent, normalizeBannerObject, toRenderableText };
